import type { ChangeEventHandler, FocusEventHandler } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PreferredLogin } from '../types';
import { getPhonePrefixFromDriverCardCountry } from '../utils/phonePrefix';
import type { DriverCardCountry } from '../utils/driverCardCountry';
import { isAustrianPhoneNumber, isSpanishPhoneNumber } from '../utils/validators';

interface Props {
    value: string;
    inputType: 'phone' | 'email';
    inputErrors: { email: boolean; phoneFormat: boolean; phoneCountryCode: boolean };
    preferredLogin: PreferredLogin;
    handleRadioSelection: () => void;
    handleSelectionStart: () => void;
    handleInput: ChangeEventHandler<HTMLInputElement>;
    handleBlur: FocusEventHandler<HTMLInputElement>;
    showValidation: boolean;
    isPending: boolean;
    driverCardCountry?: DriverCardCountry;
}

export const SecondScreenInput = ({
    value,
    inputErrors,
    preferredLogin,
    handleRadioSelection,
    handleSelectionStart,
    handleInput,
    handleBlur,
    showValidation,
    inputType,
    isPending,
    driverCardCountry,
}: Props) => {
    const intl = useIntl();
    const isSpanishPhone = inputType === 'phone' && isSpanishPhoneNumber(value.trim());
    const isAustrianPhone = inputType === 'phone' && isAustrianPhoneNumber(value.trim());
    const isBlockedPhone = isSpanishPhone || isAustrianPhone;

    const hasError =
        showValidation &&
        ((inputType === 'phone' && (isBlockedPhone || inputErrors.phoneFormat || inputErrors.phoneCountryCode)) ||
            (inputType === 'email' && inputErrors.email));

    const phonePrefix = driverCardCountry ? getPhonePrefixFromDriverCardCountry(driverCardCountry) : '';

    return (
        <div className={`form-group width-100pct${hasError ? ' has-error has-feedback' : ''}`}>
            <label className={'control-label padding-left-25 display-flex justify-content-between align-items-end'}>
                <span className={'text-size-14 line-height-125rel'}>
                    <FormattedMessage id={`intl-msg:driverRegistration.page2.${inputType}.label`} />
                </span>
                {inputType === 'phone' && (
                    <span className={'padding-bottom-1 text-italic white-space-nowrap margin-left-10'}>
                        <span className={'margin-right-3'}>
                            <FormattedMessage id={'intl-msg:driverRegistration.page2.phone.label.example'} />
                        </span>
                        <span>{phonePrefix ? phonePrefix : '+49'}</span>
                        <span>{'xxxxxxx'}</span>
                    </span>
                )}
            </label>
            <div className={'display-flex'}>
                <div
                    className={'margin-right-5 display-flex align-items-center'}
                    onPointerDownCapture={handleSelectionStart}
                >
                    <label className={'radio'}>
                        <input
                            type={'radio'}
                            name={'loginChoice'}
                            value={inputType}
                            checked={preferredLogin === inputType}
                            onChange={handleRadioSelection}
                            disabled={isPending}
                        />
                        <span className={'radio-text'} data-cy={`radio-button-${inputType}`} />
                    </label>
                </div>
                <div className={'flex-1-1'}>
                    <div>
                        <input
                            type={'text'}
                            className={'form-control input-lg'}
                            placeholder={intl.formatMessage({
                                id: `intl-msg:driverRegistration.page2.${inputType}.placeholder`,
                            })}
                            onChange={handleInput}
                            onBlur={handleBlur}
                            aria-invalid={hasError}
                            aria-describedby={hasError ? `${inputType}-error` : undefined}
                            value={value}
                            data-cy={`${inputType}-input`}
                            id={`${inputType}-input`}
                            spellCheck={false}
                            onPointerDown={handleSelectionStart}
                            onClick={handleRadioSelection}
                            readOnly={preferredLogin !== inputType || isPending}
                        />
                        {hasError && <span className={'form-control-feedback uikit-icon icon-error-sign'} />}
                    </div>
                    {hasError && (
                        <span className={'help-block'} id={`${inputType}-error`} aria-live='polite'>
                            {isSpanishPhone && (
                                <span data-cy={'spanish-phone-warning'}>
                                    <FormattedMessage id={'intl-msg:driverRegistration.page2.phone.spanishNumber.warning'} />
                                </span>
                            )}
                            {isAustrianPhone && (
                                <span data-cy={'austrian-phone-warning'}>
                                    <FormattedMessage id={'intl-msg:driverRegistration.page2.phone.austrianNumber.warning'} />
                                </span>
                            )}
                            {!isBlockedPhone && (
                                <FormattedMessage id={`intl-msg:driverRegistration.page2.${inputType}.validationError`} />
                            )}
                            {!isBlockedPhone && inputType === 'phone' && inputErrors.phoneFormat && (
                                <div>
                                    <FormattedMessage
                                        id={`intl-msg:driverRegistration.page2.${inputType}.validationError.explanation`}
                                        values={{ initialCharacter: '+' }}
                                    />
                                    <br />
                                    <FormattedMessage
                                        id={`intl-msg:driverRegistration.page2.${inputType}.validationError.example`}
                                        values={{ example: `${phonePrefix ? phonePrefix : '+49'}17199999999` }}
                                    />
                                </div>
                            )}
                            {!isBlockedPhone && inputType === 'phone' && inputErrors.phoneCountryCode && (
                                <div>
                                    <FormattedMessage
                                        id={`intl-msg:driverRegistration.page2.${inputType}.countryCode.validationError.explanation`}
                                        values={{ initialCharacter: '+' }}
                                    />
                                </div>
                            )}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};
